<template>
    <div class="modal fade" id="gateway_contract_modal" data-backdrop="static" data-keyboard="false" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        قرارداد ارائه خدمات پرداخت
                        <small v-if="contract" class="text-muted mr-2 en_num">({{ contract.contract_number }})</small>
                    </h5>
                    <button v-if="step !== 'success'" type="button" class="close mr-auto ml-0" @click="close" :disabled="busy">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="modal-body">
                    <!-- Loading -->
                    <div v-if="step === 'loading'" class="text-center py-5">
                        <span class="spinner-border text-success"></span>
                        <div class="mt-3 text-muted">در حال آماده‌سازی قرارداد...</div>
                    </div>

                    <!-- Generation error -->
                    <div v-else-if="step === 'error'" class="py-3">
                        <div class="alert alert-danger mb-3">{{ error }}</div>
                        <ul v-if="missing.length" class="mb-3">
                            <li v-for="item in missing" :key="item">{{ item }}</li>
                        </ul>
                        <p class="text-muted mb-0">
                            درخواست درگاه شما به‌صورت پیش‌نویس ذخیره شده است و تا امضای قرارداد ثبت نهایی نمی‌شود.
                        </p>
                    </div>

                    <!-- Review and sign -->
                    <div v-else-if="step === 'review' || step === 'otp'">
                        <div v-if="notice" class="alert alert-warning">{{ notice }}</div>

                        <div class="contract-frame border rounded mb-2">
                            <iframe v-if="pdf_url" :src="pdf_url" title="قرارداد" class="w-100 h-100 border-0"></iframe>
                            <div v-else class="d-flex h-100 align-items-center justify-content-center">
                                <span class="spinner-border text-success"></span>
                            </div>
                        </div>
                        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
                            <small class="text-muted">
                                نسخه قالب <span class="en_num">{{ contract.template_version }}</span>
                                · شناسه سند <span class="en_num" dir="ltr">{{ contract.sha256.substring(0, 12) }}</span>
                            </small>
                            <button v-if="pdf_url" type="button" class="btn btn-link btn-sm px-0" @click="openInNewTab">
                                باز کردن قرارداد در تب جدید
                            </button>
                        </div>

                        <div class="custom-control custom-checkbox mb-3">
                            <input type="checkbox" class="custom-control-input" id="contract_agree"
                                   v-model="agreed" :disabled="contract.accepted || busy">
                            <label class="custom-control-label" for="contract_agree">
                                مفاد قرارداد را به‌طور کامل مطالعه کردم و با آن موافقم.
                            </label>
                        </div>

                        <div v-if="step === 'otp'" class="otp-box border rounded p-3 mb-2">
                            <label for="contract_otp_code" class="d-block mb-2">
                                کد تأیید ارسال‌شده به شماره <span class="en_num" dir="ltr">{{ contract.mobile }}</span> را وارد کنید
                            </label>
                            <div class="d-flex flex-wrap align-items-center">
                                <input id="contract_otp_code" v-model="code" type="text" inputmode="numeric" autocomplete="one-time-code"
                                       maxlength="8" dir="ltr" class="form-control text-center en_num otp-input ml-3"
                                       :disabled="!otp_id || busy" @keyup.enter="sign">
                                <small v-if="otp_id && expires_in > 0" class="text-muted">
                                    اعتبار کد: <span class="en_num">{{ formatSeconds(expires_in) }}</span>
                                </small>
                                <small v-else class="text-danger">کد منقضی شده است، کد جدید دریافت کنید.</small>
                            </div>
                            <div v-if="debug_code" class="alert alert-info py-1 px-2 mt-2 mb-0 small">
                                محیط توسعه، پیامک ارسال نشد. کد: <b class="en_num" dir="ltr">{{ debug_code }}</b>
                            </div>
                            <button type="button" class="btn btn-link btn-sm px-0 mt-2" :disabled="resend_in > 0 || busy" @click="sendOtp">
                                <span v-if="resend_in > 0">ارسال مجدد کد تا <span class="en_num">{{ formatSeconds(resend_in) }}</span> دیگر</span>
                                <span v-else>ارسال مجدد کد</span>
                            </button>
                        </div>

                        <div v-if="error" class="alert alert-danger mb-0">{{ error }}</div>
                    </div>

                    <!-- Signed -->
                    <div v-else-if="step === 'success'" class="text-center py-4">
                        <i class="fa fa-check-circle text-success success-icon"></i>
                        <h5 class="mt-3">قرارداد امضا و درخواست درگاه با موفقیت ثبت شد</h5>
                        <div class="mt-3">
                            شماره پیگیری:
                            <b class="en_num mx-1" dir="ltr">{{ contract.tracking_code }}</b>
                        </div>
                        <small class="text-muted d-block mt-2">
                            نسخه امضاشده قرارداد در سامانه ذخیره شد و همیشه از همین مسیر قابل دریافت است.
                        </small>
                        <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
                    </div>
                </div>

                <div class="modal-footer">
                    <template v-if="step === 'error'">
                        <button type="button" class="btn btn-secondary rounded-pill px-4" @click="close">بستن</button>
                        <button type="button" class="btn btn-success rounded-pill px-4" @click="load">تلاش مجدد</button>
                    </template>
                    <template v-else-if="step === 'review'">
                        <button type="button" class="btn btn-secondary rounded-pill px-4" @click="close" :disabled="busy">انصراف</button>
                        <button type="button" class="btn btn-success rounded-pill px-4" :disabled="!agreed || busy || !pdf_url || resend_in > 0" @click="acceptAndSend">
                            <span v-if="busy" class="spinner-border spinner-border-sm"></span>
                            <span v-else-if="resend_in > 0">ارسال کد تأیید (<span class="en_num">{{ resend_in }}</span>)</span>
                            <span v-else>ارسال کد تأیید</span>
                        </button>
                    </template>
                    <template v-else-if="step === 'otp'">
                        <button type="button" class="btn btn-secondary rounded-pill px-4" @click="close" :disabled="busy">انصراف</button>
                        <button type="button" class="btn btn-success rounded-pill px-4" :disabled="!can_sign" @click="sign">
                            <span v-if="busy" class="spinner-border spinner-border-sm"></span>
                            <span v-else>ثبت درخواست</span>
                        </button>
                    </template>
                    <template v-else-if="step === 'success'">
                        <button type="button" class="btn btn-outline-success rounded-pill px-4" :disabled="busy" @click="downloadSigned">
                            دانلود قرارداد امضاشده
                        </button>
                        <button type="button" class="btn btn-success rounded-pill px-4" @click="finish">ادامه</button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from 'jquery'
import { gatewayService } from '@/common/api.service'

// Tags the backend sends when the code can no longer be used: the user needs a new one.
const DEAD_OTP_TAGS = ['otp_expired', 'otp_used', 'otp_locked', 'otp_revoked']

export default {
    name: 'GatewayContractModal',
    data() {
        return {
            shop_id: null,
            step: 'loading',
            contract: null,
            pdf_url: null,
            agreed: false,
            otp_id: null,
            code: '',
            debug_code: null,
            expires_in: 0,
            resend_in: 0,
            timer: null,
            busy: false,
            error: '',
            notice: '',
            missing: [],
            sign_result: null,
        }
    },
    computed: {
        can_sign() {
            return !this.busy && this.agreed && !!this.otp_id && this.expires_in > 0 && /^\d{4,8}$/.test(this.normalizedCode())
        },
    },
    beforeDestroy() {
        this.stopTimer()
        this.revokePdf()
        $('#gateway_contract_modal').modal('hide')
    },
    methods: {
        open(shop_id) {
            this.shop_id = shop_id
            this.reset()
            $('#gateway_contract_modal').modal('show')
            this.load()
        },
        reset() {
            this.stopTimer()
            this.revokePdf()
            Object.assign(this, {
                step: 'loading', contract: null, agreed: false, otp_id: null, code: '', debug_code: null,
                expires_in: 0, resend_in: 0, busy: false, error: '', notice: '', missing: [], sign_result: null,
            })
        },
        load(notice = '') {
            this.step = 'loading'
            this.error = ''
            this.missing = []
            this.revokePdf()

            gatewayService.prepareContract(this.shop_id)
                .then(({ data }) => {
                    this.applyState(data.data)
                    this.notice = notice
                    if (this.contract.status === 'signed') {
                        this.step = 'success'
                        return
                    }
                    this.step = this.otp_id ? 'otp' : 'review'
                    this.agreed = this.contract.accepted
                    this.loadPdf()
                })
                .catch(({ response }) => {
                    this.step = 'error'
                    this.error = this.messageOf(response, 'تولید قرارداد با خطا مواجه شد. لطفاً دوباره تلاش کنید.')
                    this.missing = (response && response.data && response.data.data && response.data.data.missing) || []
                })
        },
        loadPdf() {
            const id = this.contract.id
            gatewayService.contractPdf(id)
                .then(({ data }) => {
                    if (!this.contract || this.contract.id !== id) {
                        return
                    }
                    this.pdf_url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
                })
                .catch(() => {
                    this.error = 'نمایش فایل قرارداد ممکن نشد. لطفاً دوباره تلاش کنید.'
                })
        },
        applyState(state) {
            this.contract = state
            this.otp_id = state.otp.id
            this.expires_in = state.otp.expires_in
            this.resend_in = state.otp.resend_in
            this.startTimer()
        },
        acceptAndSend() {
            if (!this.agreed) {
                return
            }
            this.busy = true
            this.error = ''
            const accept = this.contract.accepted
                ? Promise.resolve()
                : gatewayService.acceptContract(this.contract.id, this.contract.sha256).then(({ data }) => {
                    this.contract = data.data
                })

            accept
                .then(() => this.requestOtp())
                .catch(({ response }) => this.handleError(response))
                .finally(() => { this.busy = false })
        },
        sendOtp() {
            this.busy = true
            this.error = ''
            this.requestOtp()
                .catch(({ response }) => this.handleError(response))
                .finally(() => { this.busy = false })
        },
        requestOtp() {
            return gatewayService.sendContractOtp(this.contract.id, this.contract.sha256).then(({ data }) => {
                this.applyState(data.data)
                this.debug_code = data.data.debug_code || null
                this.code = ''
                this.step = 'otp'
                this.toastr.success(data.message)
                this.$nextTick(() => {
                    const input = document.getElementById('contract_otp_code')
                    input && input.focus()
                })
            })
        },
        sign() {
            if (!this.can_sign) {
                return
            }
            this.busy = true
            this.error = ''
            gatewayService.signContract(this.contract.id, {
                otp_id: this.otp_id,
                code: this.normalizedCode(),
                sha256: this.contract.sha256,
            })
                .then(({ data }) => {
                    this.sign_result = data.data
                    this.contract = data.data
                    this.stopTimer()
                    this.step = 'success'
                })
                .catch(({ response }) => {
                    const tag = response && response.data && response.data.tag
                    // The contract is signed; only the Enamad registration needs the user.
                    if (tag && tag.indexOf('enamad_') === 0) {
                        this.hide()
                        this.$emit('enamad-error', response.data)
                        return
                    }
                    this.handleError(response)
                })
                .finally(() => { this.busy = false })
        },
        handleError(response) {
            const body = (response && response.data) || {}
            const tag = body.tag
            const data = body.data || {}

            if (tag === 'contract_changed') {
                this.load(this.messageOf(response))
                return
            }

            if (DEAD_OTP_TAGS.includes(tag)) {
                this.otp_id = null
                this.expires_in = 0
                this.code = ''
            }

            if (data.retry_in) {
                this.resend_in = data.retry_in
                this.startTimer()
            }

            let message = this.messageOf(response, 'عملیات با خطا مواجه شد. لطفاً دوباره تلاش کنید.')
            if (tag === 'otp_invalid' && data.remaining_attempts !== undefined) {
                message += ` (${data.remaining_attempts} تلاش باقی مانده)`
            }
            this.error = message
        },
        downloadSigned() {
            this.busy = true
            gatewayService.contractPdf(this.contract.id, true)
                .then(({ data }) => {
                    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', `paystar-contract-${this.contract.contract_number}.pdf`)
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    setTimeout(() => window.URL.revokeObjectURL(url), 1000)
                })
                .catch(() => {
                    this.error = 'دریافت فایل قرارداد ممکن نشد. لطفاً دوباره تلاش کنید.'
                })
                .finally(() => { this.busy = false })
        },
        openInNewTab() {
            window.open(this.pdf_url, '_blank')
        },
        finish() {
            this.hide()
            this.$emit('signed', this.sign_result || this.contract)
        },
        close() {
            this.hide()
            this.$emit('closed')
        },
        hide() {
            this.stopTimer()
            $('#gateway_contract_modal').modal('hide')
        },
        startTimer() {
            if (this.timer) {
                return
            }
            this.timer = setInterval(() => {
                if (this.expires_in > 0) this.expires_in--
                if (this.resend_in > 0) this.resend_in--
                if (this.expires_in <= 0 && this.resend_in <= 0) this.stopTimer()
            }, 1000)
        },
        stopTimer() {
            clearInterval(this.timer)
            this.timer = null
        },
        revokePdf() {
            if (this.pdf_url) {
                window.URL.revokeObjectURL(this.pdf_url)
            }
            this.pdf_url = null
        },
        normalizedCode() {
            return String(this.code || '')
                .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
                .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
                .replace(/\s+/g, '')
        },
        messageOf(response, fallback = '') {
            return (response && response.data && response.data.message) || fallback
        },
        formatSeconds(seconds) {
            const m = Math.floor(seconds / 60)
            const s = seconds % 60
            return `${m}:${s < 10 ? '0' : ''}${s}`
        },
    },
}
</script>

<style scoped>
.contract-frame {
    height: 60vh;
    min-height: 320px;
    overflow: hidden;
}

.otp-input {
    max-width: 180px;
    letter-spacing: 6px;
    font-size: 1.2rem;
}

.success-icon {
    font-size: 56px;
}
</style>
